/**
 * Created by Qingger Corp.
 * User: jsspf
 * Date: 2017/11/21
 * Time: 12:31
 */

import moment from 'moment';
import hash from 'object-hash';

import { EmpResConfig } from './config';
import cacheManager, { setEmpInfoCache } from '@/library/cache/storageCacheManager';
import { CacheKeyType } from '@/library/cache/types';
import { DataExceptionError } from '@/exception/DataException';
import { ErrCode } from '@/exception/ExceptionCode';
import baseAPIService from '@/services/base/baseAPIService';
import { HttpMethod } from '@/util/type';
import { QinggerLibURL } from '@/util/urlUtils';
import { globalElement } from '@/library/utils/globalElement';
import { AppExceptionError } from '@/exception/AppException';

/**
 * EmpService服务接口
 */
export namespace EmpServiceAPIActions {
  import EMP_PORTAL_AUTH_APIID = EmpResConfig.EMP_PORTAL_AUTH_APIID;
  import EMP_PASSWORD_AUTH_APIID = EmpResConfig.EMP_PASSWORD_AUTH_APIID;
  const EMP_PORTAL_LOGIN_URL = EmpResConfig.EMP_PORTAL_LOGIN_URL;
  const NEW_EMP_PORTAL_LOGIN_URL = EmpResConfig.NEW_EMP_PORTAL_LOGIN_URL;
  const empURLResConfig = EmpResConfig.APIResURLConfig;

  /**
   * 存储的Token结构
   */
  export interface TokenInfo {
    token: string;
    refreshTime: number;
  }

  /**
   * 获得TOKEN, 从缓存获取
   * @returns {TokenInfo}
   */
  export const getTokenFromCache = function (): TokenInfo {
    return cacheManager.getItem(CacheKeyType.CACHE_FOR_PORTAL_TOKEN);
  };

  /**
   * 设置TOKEN
   * @param {string} token
   */
  const setTokenInCache = function (token: string): void {
    if (!token) {
      cacheManager.forgetItem(CacheKeyType.CACHE_FOR_PORTAL_TOKEN);
      return null;
    }

    const tokenInfo = {
      token,
      refreshTime: moment().unix(),
    };

    cacheManager.setItem(CacheKeyType.CACHE_FOR_PORTAL_TOKEN, tokenInfo, 0);
  };

  /**
   * 获取集团信息
   */
  export function getIsNewEmp(): Promise<any> {
    const corpId = baseAPIService.resolveCorpId();
    const platformVersion = localStorage.getItem('platformVersion');
    const groupCode = localStorage.getItem('groupCode');
    const downloadState = localStorage.getItem('downloadState');
    const firstSiteId = localStorage.getItem('firstSiteId');
    const isOpenSkCart = localStorage.getItem('isOpenSkCart')
    const skPointsType = localStorage.getItem('skPointsType')
    const isOpenVisitTrend = localStorage.getItem('isOpenVisitTrend')
    if (platformVersion && groupCode && downloadState && firstSiteId && skPointsType && isOpenSkCart && isOpenVisitTrend) {
      return Promise.resolve({ groupCode, platformVersion, downloadState });
    } else {
      return baseAPIService.apiCall(empURLResConfig.GRT_GROUP_INFO, { postParams: { corpId } }).then(
        (res: any) => {
          const { platformVersion, groupCode, downloadState, firstSiteId, isOpenSkCart, skPointsType, isOpenVisitTrend } = res;
          localStorage.setItem('platformVersion', `${platformVersion}` || '');
          localStorage.setItem('groupCode', groupCode || '');
          localStorage.setItem('downloadState', downloadState || '');
          localStorage.setItem('firstSiteId', firstSiteId || '');
          localStorage.setItem('isOpenSkCart', isOpenSkCart || 0);
          localStorage.setItem('skPointsType', skPointsType || 1);
          localStorage.setItem('isOpenVisitTrend', isOpenVisitTrend || 0);
          return {
            groupCode,
            platformVersion: `${platformVersion}`,
            downloadState,
            firstSiteId,
            isOpenSkCart,
            skPointsType,
            isOpenVisitTrend
          };
        },
        (rej) => {
          console.log('获取集团信息失败！');
        },
      );
    }
  }

  /**
   * 检查Token是否需要更新
   * @param {EmpServiceAPIActions.TokenInfo} tokenInfo
   * @returns {EmpServiceAPIActions.TokenInfo | boolean}
   */
  const checkTokenNeedUpdate = function (tokenInfo: TokenInfo) {
    const expireTime = 12000;
    const currentTime = moment().unix();
    return tokenInfo && currentTime - tokenInfo.refreshTime > expireTime;
  };

  /**
   * 获得EmpService的授权码
   * @returns {string}
   */
  const getAuthCodeFromQueryParam = function () {
    return QinggerLibURL.urlUtil(window.location.href).getUrlQueryItem('code');
  };

  /**
   * 门户登录地址跳转
   * @param uid
   */
  export const redirectToPortal = function (uid) {
    const corpId = baseAPIService.resolveCorpId();
    if (!corpId) {
      throw new DataExceptionError(ErrCode.ERR_DATA_SET_ERROR, '集团ID不存在');
    }

    this.getIsNewEmp().then((res) => {
      const { platformVersion, groupCode } = res;
      const params: any = {
        target: corpId,
        groupCode,
        callbackUrl: window.location.href,
        uid,
        webSource: 'IPSSHOP',
      };

      const redirectURL = QinggerLibURL.urlUtil(platformVersion === '1' ? EMP_PORTAL_LOGIN_URL : NEW_EMP_PORTAL_LOGIN_URL).setUrlQueryParams(params);

      const reload = () => {
        // 判断接口重复调用 ，暂时只判断刷新2次
        const reloadConfig: any = localStorage.getItem('reloadConfig');
        if (reloadConfig) {
          const reloadObj: any = JSON.stringify(reload);
          // 时间过去一分钟了
          if (moment().unix() - reloadObj.saveTime > 60) {
            localStorage.removeItem('reloadConfig');
            return true;
          }
          // 刷新超过2次
          if (reloadObj.count > 2) {
            return false;
          } else {
            localStorage.setItem('reloadConfig', JSON.stringify({ saveTime: moment().unix(), count: reloadConfig.count++ }));
            return true;
          }
        } else {
          localStorage.setItem('reloadConfig', JSON.stringify({ saveTime: moment().unix(), count: 1 }));
          return true;
        }
      };
      if (reload) {
        window.location.replace(redirectURL);
      }
    });
  };

  /**
   * 调用接口刷新Token
   * @param {string} token
   * @returns {Promise<string>}
   */
  export const refreshToken = function (token: string): Promise<any> {
    if (!token) {
      globalElement.message.error('刷新TOKEN失败,应用找不到TOKEN');
      const coprId = baseAPIService.resolveCorpId();
      localStorage.clear();
      cacheManager.setItem(CacheKeyType.CACHE_FOR_CORPID, coprId, 0);
      window.location.reload();
      return;
    }
    return getIsNewEmp().then((res) => {
      const platformVersion = res.platformVersion;

      let postParams = {
        authToken: token,
        ...res,
      };

      if (platformVersion !== '1') {
        const refreshToken = localStorage.getItem('refreshToken');
        const groupCode = localStorage.getItem('groupCode');
        if (!refreshToken) {
          throw new DataExceptionError(ErrCode.ERR_POST_TOKEN_INVALID, '刷新TOKEN失败,应用找不到刷新TOKEN');
        }
        postParams = { refreshToken, groupCode, webSource: 'IPSSHOP' };
      }

      return baseAPIService
        .apiCall(platformVersion === '1' ? empURLResConfig.EMP_REFRESH_TOKEN : empURLResConfig.NEW_EMP_REFRESH_TOKEN, {
          postParams,
        })
        .then((token: { accessToken: string; expire: number; refreshToken: string } | string) => {
          const accessToken = typeof token === 'string' ? token : token.accessToken;
          if (platformVersion !== '1' && typeof token !== 'string' && token.refreshToken) {
            localStorage.setItem('refreshToken', token.refreshToken);
          }
          setTokenInCache(accessToken);
          return accessToken;
        });
    });
  };

  /**
   * 调用API接口请求Token
   * @param {string} code
   * @returns {Promise<string>}
   */
  const postToken = function (code: string) {
    const apiId = EMP_PORTAL_AUTH_APIID;
    const target = baseAPIService.resolveCorpId();
    const timestamp = moment().unix();
    const nonce = hash(timestamp);
    const apiSign = 'APISIGN';
    if (!target) {
      throw new DataExceptionError(ErrCode.ERR_POST_TOKEN_INVALID, '请求TOKEN失败,corpId错误');
    }
    return getIsNewEmp().then((res) => {
      const platformVersion = res.platformVersion;
      return baseAPIService
        .apiCall(platformVersion === '1' ? empURLResConfig.EMP_POST_TOKEN : empURLResConfig.NEW_EMP_POST_TOKEN, {
          postParams: {
            apiId,
            target,
            timestamp,
            nonce,
            apiSign,
            code,
            ...res,
          },
        })
        .then((token: { accessToken: string; expire: number; refreshToken: string } | string) => {
          const accessToken = typeof token === 'string' ? token : token.accessToken;
          setTokenInCache(accessToken);

          if (platformVersion !== '1' && typeof token !== 'string' && token.refreshToken) {
            localStorage.setItem('refreshToken', token.refreshToken);
          }

          return accessToken;
        });
    });
  };

  /**
   * 根据用户名和密码获取Token
   * @param {string} userId
   * @param {string} userPassword
   * @returns {Promise<string>}
   */
  export const postTokenWithUserPassword = function (userId: string, userPassword: string) {
    const target = baseAPIService.resolveCorpId();
    if (!target) {
      throw new DataExceptionError(ErrCode.ERR_DATA_SET_ERROR, '集团ID没有设置');
    }

    const timestamp = moment().unix();
    const nonce = hash(timestamp);
    const apiSign = 'APISIGN';
    if (!target) {
      throw new DataExceptionError(ErrCode.ERR_POST_TOKEN_INVALID, '请求TOKEN失败,corpId错误');
    }

    return getIsNewEmp().then((res) => {
      const platformVersion = res.platformVersion;
      return baseAPIService
        .apiCall(platformVersion === '1' ? empURLResConfig.EMP_POST_TOKEN : empURLResConfig.NEW_EMP_POST_TOKEN, {
          postParams: {
            apiId: EMP_PASSWORD_AUTH_APIID,
            target,
            nonce,
            timestamp,
            userId,
            userPassword,
            apiSign,
            ...res,
          },
        })
        .then((token: { accessToken: string; expire: number; refreshToken: string } | string) => {
          const accessToken = typeof token === 'string' ? token : token.accessToken;
          setTokenInCache(accessToken);
          if (platformVersion !== '1' && typeof token !== 'string' && token.refreshToken) {
            localStorage.setItem('refreshToken', token.refreshToken);
          }
          return accessToken;
        })
        .catch((err: HttpAPIRetItem) => {
          if (err.data) {
            throw new AppExceptionError(err.data as ExceptionInfo);
          }
        });
    });
  };

  export const clearEmpToken = function () {
    setTokenInCache(null);
  };

  /**
   * 请求EmpService的Token，对外服务Token通一使用此函数
   * @returns {Promise<string>}
   */
  export const requireEmpToken = function (): Promise<string> {
    const tokenInfo = getTokenFromCache();
    const authCode = getAuthCodeFromQueryParam();

    if (!tokenInfo || !tokenInfo.token) {
      if (authCode) {
        // token不存在，但URL上存在code参数,
        let url = window.location.href;
        if (url.indexOf('code=') > 0) {
          url = url.slice(0, Math.max(0, url.indexOf('code=') - 1));
        }
        window.history.replaceState({}, '0', url);
        return postToken(authCode);
      } else {
        // 如果token不存在，且不存在code参数,则跳转到门户授权
        /* throw new AppException({code:ErrCode.ERR_DATA_SET_ERROR,message:'Token不存在'}) */
        redirectToPortal(hash(moment().unix()));
        return Promise.resolve('');
      }
    } else if (checkTokenNeedUpdate(tokenInfo)) {
      // token已经超过了时长期限，刷新token
      return refreshToken(tokenInfo.token);
    } else {
      // 直接resolve(Token)
      return Promise.resolve(tokenInfo.token);
    }
  };

  /**
   * 获得Emp详情
   * @see http://git.project.qingger.com/qingger/serviceCenter/blob/master/Projects/EmpService/DevelopAPI/userLogin/tokenUserInfo.md
   * @type {PromiseAPICallFun<EmpInfo>}
   */
  // export const empAuthInfo = apiWithTokenLoader<EmpInfo>(EmpResConfig.resolveEmpURLConfig('/user/auth/empInfo',HttpMethod.GET));

  /**
   * 获取用户信息
   * @returns {Promise<EmpInfo>}
   */
  export const empAuthInfo = function (): Promise<EmpInfo> {
    const resConfig = EmpResConfig.resolveEmpURLConfig('/user/auth/empInfo', HttpMethod.GET);
    const newResConfig = EmpResConfig.resolveNewEmpURLConfig('/user/auth/empInfo', HttpMethod.GET);
    return getIsNewEmp().then((res) => {
      const platformVersion = res.platformVersion;
      return baseAPIService
        .apiCallWithToken(platformVersion === '1' ? resConfig : newResConfig, { ...res }, requireEmpToken)
        .then((res: EmpInfo) => {
          setEmpInfoCache(res);
          return res;
        })
        .catch((err) => {
          globalElement.message.error(`${err.code}:${err.message}`);
        });
    });
  };
}
